import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ClipboardList, Inbox, Loader2, HardHat, Eye, Download } from "lucide-react";
import toast from "react-hot-toast";

import { NCR_STATUSES, NCR_STATUS_MAP } from "../../constants/ncrStatus";
import {
  getNCRSummary,
  getNCRList,
  getMyPending,
  getProjectHeadPendingNCRs,
  getNCRFilterOptions,
} from "../../services/ncrService";
import StatCard from "./StatCard";
import NCRStatusBadge from "./NCRStatusBadge";
import NCRClassificationBadge from "./NCRClassificationBadge";
import NCRReportPreviewModal from "./NCRReportPreviewModal";
import { formatNCRDate, formatNCRDateTime } from "../../utils/ncrDateTime";
import { loadNCRReportBlob, downloadNCRReportFile } from "../../utils/ncrReportManager";
import { getProjectMakersForNCR } from "../../api";

const STATUS_BORDER_MAP = {
  amber: "border-l-amber-400",
  orange: "border-l-orange-400",
  purple: "border-l-purple-400",
  teal: "border-l-teal-400",
  green: "border-l-green-400",
  red: "border-l-red-400",
  blue: "border-l-blue-400",
};

/**
 * Decorative banner illustration — original SVG artwork (site silhouette,
 * crane, two hard-hat figures) built to echo the "team overlooking a build"
 * hero mood, without using any photo or real person. Purely presentational.
 */
function NCRBannerArt({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 400"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ncrSky" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDBA74" />
          <stop offset="50%" stopColor="#FB923C" />
          <stop offset="100%" stopColor="#EA580C" />
        </linearGradient>
        <linearGradient id="ncrFade" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="1200" height="400" fill="url(#ncrSky)" />

      {/* sun glow */}
      <circle cx="1010" cy="90" r="90" fill="#FFF7ED" opacity="0.35" />

      {/* faint blueprint grid */}
      <g stroke="#FFFFFF" strokeOpacity="0.12" strokeWidth="1">
        <line x1="700" y1="0" x2="700" y2="400" />
        <line x1="820" y1="0" x2="820" y2="400" />
        <line x1="940" y1="0" x2="940" y2="400" />
        <line x1="1060" y1="0" x2="1060" y2="400" />
        <line x1="1180" y1="0" x2="1180" y2="400" />
        <line x1="600" y1="80" x2="1200" y2="80" />
        <line x1="600" y1="160" x2="1200" y2="160" />
        <line x1="600" y1="240" x2="1200" y2="240" />
        <line x1="600" y1="320" x2="1200" y2="320" />
      </g>

      {/* skyline under construction */}
      <g>
        <rect x="700" y="230" width="90" height="170" fill="#7C2D12" opacity="0.32" />
        <rect x="800" y="170" width="110" height="230" fill="#7C2D12" opacity="0.42" />
        <rect x="920" y="120" width="120" height="280" fill="#431407" opacity="0.48" />
        <rect x="1050" y="200" width="100" height="200" fill="#7C2D12" opacity="0.32" />
      </g>

      {/* tower crane */}
      <g stroke="#431407" strokeWidth="4" strokeLinecap="round" opacity="0.55" fill="none">
        <line x1="980" y1="400" x2="980" y2="70" />
        <line x1="980" y1="80" x2="1160" y2="80" />
        <line x1="980" y1="80" x2="930" y2="130" />
        <line x1="1130" y1="80" x2="1130" y2="150" />
      </g>

      {/* two hard-hat figures, looking up at the build */}
      <g transform="translate(755,400)" fill="#1E293B" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
      </g>
      <g transform="translate(825,400) scale(0.92)" fill="#1E293B" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
      </g>

      {/* fade so the header text stays legible over the art */}
      <rect width="1200" height="400" fill="url(#ncrFade)" />
    </svg>
  );
}

const RESPONDABLE_STATUSES = [
  "assigned_to_maker",
  "resubmission_required",
];
const MAKER_RESPONDABLE_STATUSES = RESPONDABLE_STATUSES;

const getAssigneeUsername = (item, userMap = {}) => {
  if (!item) return "Maker";

  // 1. Direct username field if available
  const explicitUsername = (
    item.maker_username ||
    item.username ||
    item.user_name ||
    ""
  ).trim();
  if (explicitUsername) return explicitUsername;

  // 2. Map lookup by maker_id or id
  const idKey = String(item.maker_id ?? item.id ?? "");
  if (idKey && userMap[idKey]) {
    return userMap[idKey];
  }

  // 3. Fallback name cleanup
  const rawName = (item.maker_name || item.name || "").trim();
  if (!rawName) return "Maker";

  // Check if raw name maps to a known username (e.g. from project user list)
  if (userMap[rawName.toLowerCase()]) {
    return userMap[rawName.toLowerCase()];
  }

  // If name has repeated words like "KTMaker KTMaker", extract first word
  const parts = rawName.split(/\s+/);
  if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
    return parts[0];
  }

  return rawName;
};

const renderAssignees = (row, userMap = {}) => {
  const list = (row?.assignments || row?.assigned_makers || []).filter(
    (a) => a && a.is_active !== false,
  );

  if (!list.length) {
    return <span className="text-slate-400 text-xs">-</span>;
  }

  // Collect all assigned maker usernames
  const names = [];
  const seen = new Set();

  for (const item of list) {
    const uname = getAssigneeUsername(item, userMap);
    if (uname && !seen.has(uname.toLowerCase())) {
      seen.add(uname.toLowerCase());
      names.push(uname);
    }
  }

  if (!names.length) {
    return <span className="text-slate-400 text-xs">-</span>;
  }

  if (names.length === 1) {
    return (
      <span className="font-medium text-slate-800 text-sm whitespace-nowrap">
        {names[0]}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {names.map((name, idx) => (
        <span
          key={idx}
          className="font-medium text-slate-800 text-sm leading-snug whitespace-nowrap"
        >
          {name}
        </span>
      ))}
    </div>
  );
};

export default function NCRListPage({ mode = "all", role = "maker" }) {
  const navigate = useNavigate();
  const [activeStatus, setActiveStatus] = useState("all");
  const [summary, setSummary] = useState(null);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState({});

  const [filters, setFilters] = useState({
    project_id: "",
    tower_id: "",
    classification: "",
    from_date: "",
    to_date: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    projects: [],
    towers: [],
    classifications: [
      { id: "major", name: "Major" },
      { id: "minor", name: "Minor" },
    ],
  });

  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  const [downloadingReportId, setDownloadingReportId] = useState(null);

  const handleDownloadReport = async (row) => {
    if (downloadingReportId || !row?.id) return;
    if (row.status !== "closed") {
      toast.error("Report can be downloaded only after NCR is closed.");
      return;
    }
    setDownloadingReportId(row.id);
    try {
      await downloadNCRReportFile(row);
      toast.success("NCR report downloaded.");
    } catch (err) {
      console.error("Failed to download NCR report", err);
      toast.error(err?.message || "Failed to download NCR report.");
    } finally {
      setDownloadingReportId(null);
    }
  };

  let roleStr = localStorage.getItem("ROLE") || "";
  if (!roleStr) {
    try {
      const raw = localStorage.getItem("USER_DATA");
      if (raw) {
        const data = JSON.parse(raw);
        roleStr = data?.role || data?.roles?.[0] || "";
      }
    } catch (e) {}
  }
  const isMaker = (roleStr || "").toLowerCase().includes("maker");

  // Load filter options
  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      setFilterOptionsLoading(true);

      try {
        const res = await getNCRFilterOptions();
        const payload = res?.data || res;

        if (!cancelled) {
          setFilterOptions((prev) => ({
            ...prev,
            projects: payload?.projects || [],
            towers: payload?.towers || [],
            classifications: payload?.classifications || prev.classifications,
          }));
        }
      } catch (err) {
        console.error("Failed to load NCR filter options", err);
        toast.error("Failed to load NCR filters");
      } finally {
        if (!cancelled) {
          setFilterOptionsLoading(false);
        }
      }
    };

    loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "project_id" ? { tower_id: "" } : {}),
    }));
  };

  const clearFilters = () => {
    setFilters({
      project_id: "",
      tower_id: "",
      classification: "",
      from_date: "",
      to_date: "",
    });
  };

  const hasInvalidDateRange = Boolean(
    filters.from_date &&
    filters.to_date &&
    filters.from_date > filters.to_date
  );

  const getScopeFilterParams = () => {
    const params = {};

    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.tower_id) params.tower_id = filters.tower_id;
    if (filters.classification) params.classification = filters.classification;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;

    return params;
  };

  const buildServerParams = () => {
    const params = {
      ...getScopeFilterParams(),
      page: 1,
      pageSize: 50,
    };

    if (activeStatus && activeStatus !== "all") {
      if (activeStatus === "overdue") {
        params.bucket = "overdue";
      } else if (activeStatus === "pending") {
        params.bucket = "pending";
      } else if (activeStatus === "pending_verification") {
        params.bucket = "pending_verification";
      } else if (activeStatus === "project_head_pending") {
        params.bucket = "project_head_pending";
      } else {
        params.status = activeStatus;
      }
    }

    return params;
  };

  // Fetch summary
  useEffect(() => {
    if (hasInvalidDateRange) return;

    const params = getScopeFilterParams();

    getNCRSummary(params)
      .then((res) => {
        setSummary(res?.data || res);
      })
      .catch((err) => {
        console.error("Failed to load NCR summary", err);
        toast.error("Failed to load NCR summary");
      });
  }, [
    filters.project_id,
    filters.tower_id,
    filters.classification,
    filters.from_date,
    filters.to_date,
    hasInvalidDateRange,
  ]);

  // Fetch list when filters or activeStatus change
  useEffect(() => {
    if (hasInvalidDateRange) {
      toast.error("From date cannot be greater than To date.");
      setListData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const params = buildServerParams();

    const fetchFunc = () => {
      if (mode === "my-pending") {
        return getMyPending({ role, ...params });
      }

      if (mode === "project-head-pending") {
        return getProjectHeadPendingNCRs(params);
      }

      return getNCRList(params);
    };

    fetchFunc()
      .then((res) => {
        const payload = res?.data || res;
        setListData(payload?.results || payload?.data || payload || []);
      })
      .catch((err) => {
        console.error("Failed to load NCR list", err);
        toast.error("Failed to load NCR list");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    activeStatus,
    mode,
    role,
    filters.project_id,
    filters.tower_id,
    filters.classification,
    filters.from_date,
    filters.to_date,
    hasInvalidDateRange,
  ]);

  // Resolve usernames for any projects present in the list
  useEffect(() => {
    if (!listData || !listData.length) return;

    const projectIds = Array.from(
      new Set(listData.map((row) => row.project_id).filter(Boolean))
    );

    if (!projectIds.length) return;

    let cancelled = false;

    const loadUsersForProjects = async () => {
      const newMap = {};
      await Promise.all(
        projectIds.map(async (pid) => {
          try {
            const res = await getProjectMakersForNCR(pid);
            const data = res?.data ?? res;
            const list = Array.isArray(data)
              ? data
              : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.data)
              ? data.data
              : [];

            list.forEach((item) => {
              const userObj =
                item?.user || item?.user_details || item?.employee || item;
              const uid =
                item?.user_id ??
                item?.maker_id ??
                userObj?.id ??
                userObj?.user_id ??
                item?.id;
              const username = (
                userObj?.username ||
                item?.username ||
                userObj?.user_name ||
                item?.user_name ||
                ""
              ).trim();

              const firstName = (
                userObj?.first_name ??
                item?.first_name ??
                ""
              ).trim();
              const lastName = (
                userObj?.last_name ??
                item?.last_name ??
                ""
              ).trim();
              const fullName = `${firstName} ${lastName}`.trim().toLowerCase();

              if (username) {
                if (uid) newMap[String(uid)] = username;
                if (fullName) newMap[fullName] = username;
                const displayName = (
                  userObj?.full_name ||
                  userObj?.name ||
                  item?.name ||
                  ""
                )
                  .trim()
                  .toLowerCase();
                if (displayName) newMap[displayName] = username;
              }
            });
          } catch (e) {
            // Ignore error for individual project
          }
        })
      );

      if (!cancelled && Object.keys(newMap).length > 0) {
        setUserMap((prev) => ({ ...prev, ...newMap }));
      }
    };

    loadUsersForProjects();

    return () => {
      cancelled = true;
    };
  }, [listData]);

  const toggleStatus = (statusKey) => {
    if (activeStatus === statusKey) {
      setActiveStatus("all");
    } else {
      setActiveStatus(statusKey);
    }
  };

  const getNCRRowPath = (row) => {
    const status = row?.status;

    if (
      isMaker &&
      MAKER_RESPONDABLE_STATUSES.includes(status)
    ) {
      return `/ncr/${row.id}/respond`;
    }

    if (
      mode === "my-pending" &&
      role === "maker" &&
      MAKER_RESPONDABLE_STATUSES.includes(status)
    ) {
      return `/ncr/${row.id}/respond`;
    }

    if (mode === "project-head-pending") {
      return `/ncr/${row.id}`;
    }

    if (!isMaker && status === "maker_submitted") {
      // Current user ID
      let currentUserId = null;
      try {
        const rawUser = localStorage.getItem("USER_DATA");
        if (rawUser) {
          const parsed = JSON.parse(rawUser);
          currentUserId = parsed?.id || parsed?.user_id || parsed?.pk;
        }
      } catch (e) {}

      // Only the creator who raised the NCR (or admin) should navigate directly to verify
      if (currentUserId && row?.raised_by_id && Number(row.raised_by_id) !== Number(currentUserId)) {
        return `/ncr/${row.id}`;
      }
      return `/ncr/${row.id}/verify`;
    }

    return `/ncr/${row.id}`;
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 md:space-y-8 animate-in fade-in duration-300">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
        <NCRBannerArt className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" />
        <div className="relative flex flex-wrap items-end justify-between gap-6 px-6 py-8 md:px-10 md:py-12">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-2">
              <HardHat className="w-3.5 h-3.5" />
              Quality Management
            </div>
            <h1
              className="text-3xl md:text-4xl font-display font-extrabold tracking-tight text-slate-900"
              data-testid="ncr-list-title"
            >
              {mode === "my-pending"
                ? "Your Pending NCRs"
                : mode === "project-head-pending"
                  ? "NCRs Pending Your Signature"
                  : "Non-Conformity Reports"}
            </h1>
            <p className="text-sm md:text-base text-slate-600 mt-2 max-w-md">
              {mode === "my-pending"
                ? "NCRs awaiting your action"
                : mode === "project-head-pending"
                  ? "Sign formal NCR approvals"
                  : "Manage and track all NCRs across projects."}
            </p>
          </div>
          {!isMaker && (
            <button
              onClick={() => navigate("/ncr/create")}
              data-testid="new-ncr-btn"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white bg-orange-600 rounded-lg shadow-md shadow-orange-600/20 transition-all duration-150 hover:bg-orange-700 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <Plus className="w-4 h-4" /> New NCR
            </button>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 items-stretch auto-rows-fr">
        {loading && !summary ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-full min-h-[112px] bg-white rounded-2xl border border-slate-200 p-4 animate-pulse"
            >
              <div className="w-10 h-10 rounded-xl bg-slate-200 mb-4"></div>
              <div className="w-1/2 h-6 bg-slate-200 rounded mb-2"></div>
              <div className="w-1/3 h-3 bg-slate-200 rounded"></div>
            </div>
          ))
        ) : (
          <>
            <StatCard
              icon={ClipboardList}
              label={mode === "my-pending" ? "Pending NCRs" : "All NCRs"}
              count={summary ? summary.total : "-"}
              colorKey="blue"
              active={activeStatus === "all"}
              onClick={() => toggleStatus("all")}
              testId="stat-card-all"
              className="animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: "0ms", animationFillMode: "both" }}
            />
            {NCR_STATUSES.map((s, i) => (
              <StatCard
                key={s.key}
                icon={s.icon}
                label={s.label}
                count={summary ? summary[s.key] || 0 : "-"}
                colorKey={s.color}
                active={activeStatus === s.key}
                onClick={() => toggleStatus(s.key)}
                testId={`stat-card-${s.key}`}
                className="animate-in fade-in slide-in-from-bottom-2"
                style={{
                  animationDelay: `${(i + 1) * 50}ms`,
                  animationFillMode: "both",
                }}
              />
            ))}
          </>
        )}
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Filters
            </h3>
            <p className="text-xs text-slate-500">
              Filter NCR records using server-side query parameters.
            </p>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Clear Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Project
            </label>
            <select
              value={filters.project_id}
              onChange={(e) => handleFilterChange("project_id", e.target.value)}
              disabled={filterOptionsLoading}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">
                {filterOptionsLoading ? "Loading projects..." : "All Projects"}
              </option>

              {(filterOptions.projects || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Tower
            </label>
            <select
              value={filters.tower_id}
              onChange={(e) => handleFilterChange("tower_id", e.target.value)}
              disabled={!filters.project_id || filterOptionsLoading}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">
                {!filters.project_id ? "Select project first" : "All Towers"}
              </option>

              {(filterOptions.towers || [])
                .filter((tower) =>
                  !filters.project_id ||
                  String(tower.project_id || tower.project) === String(filters.project_id)
                )
                .map((tower) => (
                  <option key={tower.id} value={tower.id}>
                    {tower.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Class
            </label>
            <select
              value={filters.classification}
              onChange={(e) => handleFilterChange("classification", e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">All Classes</option>
              <option value="major">Major</option>
              <option value="minor">Minor</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            />
          </div>
        </div>

        {hasInvalidDateRange && (
          <p className="mt-2 text-xs font-medium text-red-600">
            From date cannot be greater than To date.
          </p>
        )}
      </div>

      {/* List Section */}
      <div
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm transition-opacity duration-150"
        style={{ opacity: loading ? 0.6 : 1 }}
      >
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
          <h2 className="text-sm font-bold font-display text-slate-900 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            {activeStatus === "all"
              ? (mode === "my-pending" || mode === "project-head-pending"
                  ? "Pending Records"
                  : "All Records")
              : NCR_STATUSES.find((s) => s.key === activeStatus)?.label}
          </h2>
          <div className="text-xs text-slate-500 font-medium">
            {listData.length} entries found
          </div>
        </div>

        {loading && listData.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center text-slate-400 space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
            <p className="text-sm">Loading records...</p>
          </div>
        ) : listData.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center text-slate-500 space-y-4">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
              <Inbox className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm">No NCRs found for this status.</p>
            {mode === "my-pending" ? (
              <button
                onClick={() => navigate("/NCR")}
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                Go to All Records
              </button>
            ) : activeStatus !== "all" ? (
              <button
                onClick={() => setActiveStatus("all")}
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                View all NCRs
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 bg-white">
                      <th className="px-4 md:px-6 py-3.5 font-semibold">NCR No</th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">
                        Location
                      </th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">
                        Assignee
                      </th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">Class</th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">
                        Target Date
                      </th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">Status</th>
                      <th className="px-4 md:px-6 py-3.5 font-semibold">
                        Created Date
                      </th>
                    </tr>
                  </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {listData.map((row) => {
                    const statusColor =
                      NCR_STATUS_MAP[row.status?.toLowerCase()]?.color ||
                      NCR_STATUSES.find((s) => s.key === row.status)?.color;
                    const borderClass =
                      STATUS_BORDER_MAP[statusColor] || "border-l-transparent";
                    return (
                      <tr
                        key={row.id}
                        onClick={() => navigate(getNCRRowPath(row))}
                        className={`hover:bg-orange-50/40 cursor-pointer transition-colors duration-150 border-l-4 ${borderClass}`}
                        data-testid={`ncr-row-${row.ncr_no}`}
                      >
                        <td className="px-4 md:px-6 py-4 font-mono font-medium text-slate-900">
                          {row.ncr_no}
                        </td>
                        <td className="px-4 md:px-6 py-4 text-slate-600">
                          {row.non_conformity_related_to ||
                            row.full_location ||
                            "-"}
                        </td>

                        <td className="px-4 md:px-6 py-3.5 text-slate-700">
                          {renderAssignees(row, userMap)}
                        </td>

                        <td className="px-4 md:px-6 py-4">
                          <NCRClassificationBadge
                            classification={row.classification}
                          />
                        </td>

                        <td className="px-4 md:px-6 py-4 text-slate-600">
                          {formatNCRDate(row.target_date_of_compliance)}
                        </td>

                        <td className="px-4 md:px-6 py-4">
                          <NCRStatusBadge status={row.status} />
                        </td>
                        <td className="px-4 md:px-6 py-4 text-slate-500">
                          {formatNCRDateTime(row.raised_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Stacked Card View */}
            <div className="sm:hidden flex flex-col divide-y divide-slate-100">
              {listData.map((row) => {
                const statusColor =
                  NCR_STATUS_MAP[row.status?.toLowerCase()]?.color ||
                  NCR_STATUSES.find((s) => s.key === row.status)?.color;
                const borderClass =
                  STATUS_BORDER_MAP[statusColor] || "border-l-transparent";
                return (
                  <div
                    key={row.id}
                    onClick={() => navigate(getNCRRowPath(row))}
                    className={`p-4 cursor-pointer hover:bg-orange-50/40 bg-white border-l-4 ${borderClass} transition-colors duration-150`}
                    data-testid={`ncr-mobile-card-${row.ncr_no}`}
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <span className="font-mono font-bold text-slate-900">
                        {row.ncr_no}
                      </span>
                      <NCRStatusBadge status={row.status} />
                    </div>
                    <div className="text-sm text-slate-700 font-medium mb-1">
                      {row.non_conformity_related_to ||
                        row.full_location ||
                        "-"}
                    </div>
                    <div className="text-xs text-slate-600 mb-2 flex items-start gap-2">
                      <span className="text-slate-400 font-medium shrink-0 pt-0.5">Assignee:</span>
                      {renderAssignees(row, userMap)}
                    </div>
                    <div className="flex justify-between items-center text-xs text-slate-500">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${row.classification === "major" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}
                      >
                        {row.classification}
                      </span>
                      <span>
                        Target: {formatNCRDate(row.target_date_of_compliance)}
                      </span>
                    </div>
                    {row.status === "closed" && (
                      <div
                        className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => handleDownloadReport(row)}
                          disabled={downloadingReportId === row.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 shadow-sm disabled:opacity-50"
                        >
                          {downloadingReportId === row.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Download size={13} />
                          )}
                          Download Report
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      
    </div>
  );
}